import exifr from "exifr";

/**
 * Minimal, safe EXIF extractor.
 *
 * Rules:
 * - NO locale formatting
 * - NO timezone conversion
 * - DateTimeOriginal is treated as LOCAL-NAIVE
 * - GPS is extracted if present
 */
export async function extractExifData(file) {
  try {
    const exif = await exifr.parse(file, { gps: true });
    if (!exif) return null;

    const datetimeOriginal = normalizeExifDateTimeOriginal(exif.DateTimeOriginal);

    const gps =
      Number.isFinite(exif.latitude) && Number.isFinite(exif.longitude)
        ? {
            lat: exif.latitude,
            lng: exif.longitude,
          }
        : null;

    const device =
      exif.Make || exif.Model
        ? {
            make: exif.Make || null,
            model: exif.Model || null,
          }
        : null;

    return {
      datetimeOriginal,
      gps,
      device,
      source: "exif",
    };
  } catch {
    // Silent by design: EXIF extraction is a nice-to-have. Callers that need
    // structured telemetry should use extractGpsCoordsFromImageBuffer instead,
    // which returns a discriminated skip reason.
    return null;
  }
}

// Buffer-first GPS extractor used by the catch-post selection flow. Consumes
// the stabilized ArrayBuffer produced by the Android snapshot pipeline so it
// doesn't re-read the picker handle. Returns a discriminated result; never
// throws, never logs. The caller decides what telemetry to emit.
const JPEG_MIME_TYPES = new Set(["image/jpeg", "image/jpg", "image/pjpeg"]);

export async function extractGpsCoordsFromImageBuffer(buffer, options = {}) {
  const mimeType =
    typeof options.mimeType === "string" ? options.mimeType.toLowerCase() : "";

  if (!JPEG_MIME_TYPES.has(mimeType)) {
    return { ok: false, reason: "unsupported_mime" };
  }

  if (!buffer || typeof buffer !== "object") {
    return { ok: false, reason: "no_exif" };
  }

  // Probe the TIFF header directly for the 0x8825 (GPSInfo) pointer in IFD0.
  // This is cheap (≈200 bytes read), runs before exifr, and gives us a
  // definitive answer to "did this JPEG even declare a GPS IFD?" that we can
  // put in telemetry. exifr does not expose this offset anywhere in its
  // public output.
  const gpsIfdOffset = findGpsIfdOffset(buffer);

  let exif = null;
  try {
    exif = await exifr.parse(buffer, { gps: true });
  } catch {
    return { ok: false, reason: "parse_exception" };
  }

  if (!exif) {
    return { ok: false, reason: "no_exif" };
  }

  // exifr is *supposed* to collapse GPSLatitude/GPSLongitude rationals +
  // GPSLatitudeRef/GPSLongitudeRef into signed decimal latitude/longitude at
  // the root of the output. In practice exifr's parseGpsBlock calls
  //   ConvertDMSToDD(...gps.get(TAG_GPS_LAT), gps.get(TAG_GPS_LATREF))
  // which spreads the DMS array; if the array has length !== 3 (Samsung
  // single-rational encodings, 2-element partials, BYTE-typed arrays) the
  // spread throws TypeError, exifr's safeParse swallows it with
  // silentErrors:true, and the `latitude`/`longitude` string keys never get
  // set on the output — even though the raw GPSLatitude/Longitude tags are
  // still populated from parseTags. We handle that by re-deriving from the
  // raw tags. No second exifr invocation.
  const exifLatitude = numericOrNull(exif.latitude);
  const exifLongitude = numericOrNull(exif.longitude);

  const rawLatTag = exif.GPSLatitude;
  const rawLonTag = exif.GPSLongitude;
  const rawLatRefSource = exif.GPSLatitudeRef;
  const rawLonRefSource = exif.GPSLongitudeRef;

  // Canonicalize refs to a single letter from {N,S,E,W} or null.
  // `null` means "we did not find a usable hemisphere ref." Handles:
  //   - ASCII string 'N', 'S', 'E', 'W' (with optional null terminator or
  //     trailing whitespace from legacy encoders)
  //   - Uint8Array / array-of-byte-values (some Samsung HW encoders write
  //     the ref as BYTE/UNDEFINED type instead of ASCII, giving us a
  //     typed-array-of-ASCII-codes)
  //   - scalar numeric byte code (0x4E/0x53/0x45/0x57)
  //   - undefined / empty / not-a-hemisphere-letter → null
  const latRef = resolveHemisphereRef(rawLatRefSource, "lat");
  const lonRef = resolveHemisphereRef(rawLonRefSource, "lon");

  // If the GPS IFD pointer is missing AND neither exifr collapsed nor raw
  // tags exist, there is no GPS info in this file.
  if (
    gpsIfdOffset === null &&
    exif.latitude === undefined &&
    exif.longitude === undefined &&
    rawLatTag === undefined &&
    rawLonTag === undefined
  ) {
    return { ok: false, reason: "no_gps_ifd" };
  }

  // Preferred path: trust exifr's internal DMS→DD if it gave us a finite
  // number. If refs were missing, exifr silently returned a positive value;
  // we adopt the same convention below in the raw path.
  let latitude = exifLatitude;
  let longitude = exifLongitude;
  let source = latitude !== null && longitude !== null ? "exifr" : null;

  if (latitude === null) {
    latitude = convertDmsToDecimal(rawLatTag, latRef);
  }
  if (longitude === null) {
    longitude = convertDmsToDecimal(rawLonTag, lonRef);
  }
  if (source === null && latitude !== null && longitude !== null) {
    source = "gps_ifd";
  }

  if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) {
    return {
      ok: false,
      reason: "non_finite",
      debug: {
        gpsIfdOffset,
        rawLatitude: describeRaw(rawLatTag),
        rawLongitude: describeRaw(rawLonTag),
        rawLatitudeRef: describeRef(rawLatRefSource),
        rawLongitudeRef: describeRef(rawLonRefSource),
        resolvedLatRef: latRef,
        resolvedLonRef: lonRef,
        computedLatitude: describeRaw(exif.latitude),
        computedLongitude: describeRaw(exif.longitude),
        latitudeType: typeofDescription(rawLatTag),
        latitudeIsArrayLike: isArrayLike(rawLatTag),
        latitudeLength: arrayLikeLength(rawLatTag),
        latitudeFirstItem: isArrayLike(rawLatTag)
          ? describeRaw(rawLatTag[0])
          : null,
      },
    };
  }
  if (latitude < -90 || latitude > 90 || longitude < -180 || longitude > 180) {
    return { ok: false, reason: "out_of_range" };
  }
  // Stripped-EXIF artifact, not a real fishing spot. The (0, 0) "null island"
  // is overwhelmingly a scrubbed-GPS marker, not a user's actual location.
  if (latitude === 0 && longitude === 0) {
    return { ok: false, reason: "zero_zero" };
  }

  return {
    ok: true,
    latitude,
    longitude,
    latRef,
    lonRef,
    source: source || "gps_ifd",
  };
}

// Defensive DMS-to-DD conversion. Handles every shape an EXIF reader might
// surface:
//   - scalar decimal degrees (already-converted)
//   - [d] single-element already-decimal
//   - [d, m] partial
//   - [d, m, s] standard decimal triple
//   - [[n,den], [n,den], [n,den]] raw rational triples
//   - Uint8Array / Uint16Array / Uint32Array of component numbers (some
//     Samsung HW encoders use BYTE/SHORT type)
//   - any of the above with null/NaN/missing components
// `ref` is already canonicalized to 'N' | 'S' | 'E' | 'W' | null by the
// caller; null means missing and is treated as positive hemisphere (mirrors
// exifr's own ConvertDMSToDD semantics so we don't silently drop coords
// from files that have valid DMS but no refs).
function convertDmsToDecimal(dms, ref) {
  if (dms == null) {
    return null;
  }

  let deg = 0;
  let min = 0;
  let sec = 0;

  if (typeof dms === "number") {
    if (!Number.isFinite(dms)) return null;
    deg = dms;
  } else if (isArrayLike(dms)) {
    const length = arrayLikeLength(dms);
    if (length === 0) return null;
    deg = extractRationalComponent(dms[0]);
    if (length >= 2) min = extractRationalComponent(dms[1]);
    if (length >= 3) sec = extractRationalComponent(dms[2]);
  } else {
    return null;
  }

  if (!Number.isFinite(deg) || !Number.isFinite(min) || !Number.isFinite(sec)) {
    return null;
  }

  const dd = deg + min / 60 + sec / 3600;
  if (!Number.isFinite(dd)) {
    return null;
  }

  return ref === "S" || ref === "W" ? -dd : dd;
}

function extractRationalComponent(value) {
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : NaN;
  }
  if (isArrayLike(value) && arrayLikeLength(value) >= 2) {
    const n = Number(value[0]);
    const d = Number(value[1]);
    if (Number.isFinite(n) && Number.isFinite(d) && d !== 0) {
      return n / d;
    }
  }
  return NaN;
}

// Resolve any hemisphere ref encoding EXIF readers surface to a single
// canonical uppercase letter 'N' | 'S' | 'E' | 'W', or null if the ref is
// missing / not a hemisphere letter. Accepts:
//   - ASCII string 'N', 'S', 'E', 'W' with optional null terminator / ws
//   - typed array (Uint8Array / Array / Buffer) of ASCII byte codes — some
//     Samsung HW encoders write refs as BYTE type (0x4E, 0) instead of
//     ASCII (0x4E, 0x00). We read the first non-null byte.
//   - scalar numeric byte code 0x4E ('N') / 0x53 ('S') / 0x45 ('E') / 0x57 ('W')
//   - undefined / null / empty / non-hemisphere → null
// `axis` is "lat" or "lon"; used only to reject ref letters that don't
// belong on this axis (e.g. 'E' as a latitude ref is garbage).
function resolveHemisphereRef(value, axis) {
  const letter = extractHemisphereLetter(value);
  if (letter === null) return null;
  if (axis === "lat") return letter === "N" || letter === "S" ? letter : null;
  if (axis === "lon") return letter === "E" || letter === "W" ? letter : null;
  return null;
}

function extractHemisphereLetter(value) {
  if (value === undefined || value === null) return null;

  if (typeof value === "string") {
    let end = value.length;
    while (end > 0 && value.charCodeAt(end - 1) === 0) {
      end -= 1;
    }
    const trimmed = value.slice(0, end).trim().toUpperCase();
    if (trimmed.length === 0) return null;
    const first = trimmed.charAt(0);
    return isHemisphereLetter(first) ? first : null;
  }

  if (typeof value === "number") {
    return byteCodeToHemisphere(value);
  }

  if (isArrayLike(value)) {
    const length = arrayLikeLength(value);
    for (let i = 0; i < length; i++) {
      const byte = value[i];
      if (byte === 0 || byte === undefined || byte === null) continue;
      if (typeof byte === "number") {
        const letter = byteCodeToHemisphere(byte);
        if (letter !== null) return letter;
      } else if (typeof byte === "string") {
        const trimmed = byte.trim().toUpperCase();
        const first = trimmed.charAt(0);
        if (isHemisphereLetter(first)) return first;
      }
    }
    return null;
  }

  return null;
}

function byteCodeToHemisphere(code) {
  if (!Number.isFinite(code)) return null;
  // ASCII: 'N'=0x4E, 'S'=0x53, 'E'=0x45, 'W'=0x57 (case-insensitive: 'n'=0x6E etc.)
  switch (code) {
    case 0x4e:
    case 0x6e:
      return "N";
    case 0x53:
    case 0x73:
      return "S";
    case 0x45:
    case 0x65:
      return "E";
    case 0x57:
    case 0x77:
      return "W";
    default:
      return null;
  }
}

function isHemisphereLetter(ch) {
  return ch === "N" || ch === "S" || ch === "E" || ch === "W";
}

function isArrayLike(value) {
  if (value == null) return false;
  if (Array.isArray(value)) return true;
  if (typeof value === "string") return false;
  if (typeof value !== "object") return false;
  const len = value.length;
  return typeof len === "number" && Number.isFinite(len) && len >= 0;
}

function arrayLikeLength(value) {
  if (!isArrayLike(value)) return null;
  return typeof value.length === "number" ? value.length : null;
}

function typeofDescription(value) {
  if (value === null) return "null";
  if (value === undefined) return "undefined";
  if (Array.isArray(value)) return "Array";
  if (ArrayBuffer.isView(value)) {
    const ctor = value.constructor && value.constructor.name;
    return ctor || "TypedArray";
  }
  return typeof value;
}

// Direct TIFF header walk: find the GPS IFD (0x8825) pointer in IFD0 without
// any third-party dependency. Returns the byte offset of the GPS sub-IFD
// relative to the TIFF base, or null if we couldn't find one. Bounded, never
// throws, never mutates the buffer. ~40 lines of byte arithmetic; it's not
// a full EXIF parser, it's a diagnostic probe to answer "did this file
// declare a GPS IFD at all?" for telemetry.
function findGpsIfdOffset(buffer) {
  try {
    const bytes = normalizeToUint8(buffer);
    if (!bytes || bytes.length < 12) return null;

    // JPEG SOI marker 0xFFD8.
    if (bytes[0] !== 0xff || bytes[1] !== 0xd8) return null;

    // Scan APP segments for APP1 with "Exif\0\0" identifier.
    let i = 2;
    let tiffBase = -1;
    const limit = Math.min(bytes.length - 4, 65536);
    while (i < limit) {
      if (bytes[i] !== 0xff) break;
      const marker = bytes[i + 1];
      if (marker === 0xda) break; // Start of Scan — past metadata.
      const segLength = (bytes[i + 2] << 8) | bytes[i + 3];
      if (segLength < 2) break;
      if (
        marker === 0xe1 &&
        bytes[i + 4] === 0x45 && // 'E'
        bytes[i + 5] === 0x78 && // 'x'
        bytes[i + 6] === 0x69 && // 'i'
        bytes[i + 7] === 0x66 && // 'f'
        bytes[i + 8] === 0x00 &&
        bytes[i + 9] === 0x00
      ) {
        tiffBase = i + 10;
        break;
      }
      i += 2 + segLength;
    }
    if (tiffBase < 0 || tiffBase + 8 > bytes.length) return null;

    // Byte order: 'II' (0x4949) little endian, 'MM' (0x4d4d) big endian.
    const bo0 = bytes[tiffBase];
    const bo1 = bytes[tiffBase + 1];
    let le;
    if (bo0 === 0x49 && bo1 === 0x49) le = true;
    else if (bo0 === 0x4d && bo1 === 0x4d) le = false;
    else return null;

    const readU16 = (offset) =>
      le
        ? bytes[offset] | (bytes[offset + 1] << 8)
        : (bytes[offset] << 8) | bytes[offset + 1];
    const readU32 = (offset) =>
      le
        ? (bytes[offset] |
            (bytes[offset + 1] << 8) |
            (bytes[offset + 2] << 16) |
            (bytes[offset + 3] << 24)) >>>
          0
        : ((bytes[offset] << 24) |
            (bytes[offset + 1] << 16) |
            (bytes[offset + 2] << 8) |
            bytes[offset + 3]) >>>
          0;

    const ifd0Offset = readU32(tiffBase + 4);
    const ifd0Abs = tiffBase + ifd0Offset;
    if (ifd0Abs + 2 > bytes.length) return null;

    const entryCount = readU16(ifd0Abs);
    if (entryCount <= 0 || entryCount > 512) return null;
    const entriesStart = ifd0Abs + 2;
    if (entriesStart + entryCount * 12 > bytes.length) return null;

    for (let idx = 0; idx < entryCount; idx++) {
      const entry = entriesStart + idx * 12;
      const tag = readU16(entry);
      if (tag === 0x8825) {
        const gpsRelOffset = readU32(entry + 8);
        return gpsRelOffset;
      }
    }
    return null;
  } catch {
    return null;
  }
}

function normalizeToUint8(buffer) {
  if (!buffer) return null;
  if (buffer instanceof Uint8Array) return buffer;
  if (typeof ArrayBuffer !== "undefined" && buffer instanceof ArrayBuffer) {
    return new Uint8Array(buffer);
  }
  if (ArrayBuffer.isView(buffer)) {
    return new Uint8Array(buffer.buffer, buffer.byteOffset, buffer.byteLength);
  }
  if (typeof buffer === "object" && buffer.buffer instanceof ArrayBuffer) {
    return new Uint8Array(
      buffer.buffer,
      buffer.byteOffset || 0,
      buffer.byteLength || buffer.buffer.byteLength
    );
  }
  return null;
}

const numericOrNull = (value) => {
  if (value === undefined || value === null) return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

const round5 = (v) => Math.round(v * 1e5) / 1e5;

// Compact description for diagnostic telemetry. Rounds numbers, truncates
// strings, keeps at most the first two sub-items of arrays. Never recurses
// more than one level deep to keep the payload small.
const describeRaw = (value) => {
  if (value === undefined) return "undefined";
  if (value === null) return null;
  if (typeof value === "number") {
    return Number.isFinite(value) ? round5(value) : String(value);
  }
  if (isArrayLike(value) && typeof value !== "string") {
    const length = arrayLikeLength(value);
    const take = Math.min(3, length || 0);
    const out = [];
    for (let i = 0; i < take; i++) {
      const item = value[i];
      if (item === undefined) out.push("undefined");
      else if (item === null) out.push(null);
      else if (typeof item === "number") {
        out.push(Number.isFinite(item) ? round5(item) : String(item));
      } else if (isArrayLike(item) && typeof item !== "string") {
        const subLen = arrayLikeLength(item);
        const subTake = Math.min(2, subLen || 0);
        const sub = [];
        for (let j = 0; j < subTake; j++) {
          const n = item[j];
          sub.push(
            typeof n === "number" && Number.isFinite(n) ? round5(n) : String(n)
          );
        }
        out.push(sub);
      } else {
        out.push(typeof item);
      }
    }
    return out;
  }
  if (typeof value === "string") {
    return JSON.stringify(value.slice(0, 16));
  }
  return typeof value;
};

// Describe a ref value for telemetry. Unlike describeRaw, this keeps byte
// arrays intact so we can see the exact ASCII codes a HW encoder wrote —
// that's the single most useful piece of evidence for diagnosing
// non-ASCII / BYTE-typed ref quirks.
const describeRef = (value) => {
  if (value === undefined) return "undefined";
  if (value === null) return null;
  if (typeof value === "string") return JSON.stringify(value.slice(0, 4));
  if (typeof value === "number") return value;
  if (isArrayLike(value)) {
    const length = arrayLikeLength(value);
    const take = Math.min(4, length || 0);
    const out = [];
    for (let i = 0; i < take; i++) {
      const item = value[i];
      out.push(typeof item === "number" ? item : typeofDescription(item));
    }
    return { kind: typeofDescription(value), length, head: out };
  }
  return typeof value;
};

function normalizeExifDateTimeOriginal(value) {
  if (!value) {
    return null;
  }

  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : formatLocalNaiveDate(value);
  }

  if (typeof value !== "string") {
    return null;
  }

  const trimmed = value.trim();
  if (!trimmed) {
    return null;
  }

  const exifStyleMatch = trimmed.match(
    /^(\d{4})[:.-](\d{2})[:.-](\d{2})[ T](\d{2}):(\d{2})(?::(\d{2}))?/
  );
  if (exifStyleMatch) {
    const [, year, month, day, hours, minutes, seconds = "00"] = exifStyleMatch;
    return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
  }

  const parsed = new Date(trimmed);
  if (Number.isNaN(parsed.getTime())) {
    return null;
  }

  return formatLocalNaiveDate(parsed);
}

function formatLocalNaiveDate(value) {
  const year = value.getFullYear();
  const month = String(value.getMonth() + 1).padStart(2, "0");
  const day = String(value.getDate()).padStart(2, "0");
  const hours = String(value.getHours()).padStart(2, "0");
  const minutes = String(value.getMinutes()).padStart(2, "0");
  const seconds = String(value.getSeconds()).padStart(2, "0");
  return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
}
